import {
  getCompanyEvents,
  getCompanyName,
  getRegistrationStatusForParentOrExc,
  getTitleForParentOrExc,
  isAvailableExc,
  parseEntity,
  parseSuccessorEntities,
} from './helpers/parsing'
import { timeFormat, } from '@/store/helpers.js'
import { parseRelationshipDetails, parseRelationshipDetailsForChallenge, } from '@/utils/challenge/relationship-details'

import { addPath, } from '../../../utils/address'
import { getCountryNameByCode, } from '@/utils/countries'
import isEmpty from 'lodash/isEmpty'
import { parseException, } from '@/utils/challenge/relationship-exception'
import { parseRegistrationDetails, getEntityBusinessRegistryLinkMapper, } from '@/utils/challenge/registration-details'
import record from '@/constants/tooltips/record'

const prohibitedRegStatuses = new Set([
  'ANNULLED',
  'DUPLICATE',
  'RETIRED',
  'MERGED',
  'LAPSED',
  'PENDING_TRANSFER',
  'PENDING_ARCHIVAL',
])

export default {
  recordPublishDate: ({ record, }) => record ? timeFormat(record.recordPublishDate) : null,
  lei: ({ record, }) => record ? record.lei : 'not available',
  isin: ({ isin, }) => isin,
  companyName: ({ record, }) => getCompanyName(record),
  companyEvents: ({ record, }) => getCompanyEvents(record),
  companyCountry: ({ record, }) => record ? getCountryNameByCode(record.entity.legalAddress.country) : '',
  conformityFlag: ({ record, }) => record ? record.conformityFlag : '',
  hasBEDAsManagingLou: ({ record, }) => record && record.registration.managingLou === 'EVK05KS7XY1DEII3R011',
  relationshipParentName: ({ relationship: { parent, }, }) => getCompanyName(parent),
  relationshipChildName: ({ relationship: { child, }, }) => getCompanyName(child),
  sectionsDisplay (state) {
    return [
      {
        label: 'Empty fields',
        value: state.showEmptyFields,
        property: 'showEmptyFields',
      },
      {
        label: 'Show CDF XML element names',
        value: state.showCDF,
        property: 'showCDF',
      },
      {
        label: 'Entity details',
        value: state.showEntityDetails,
        property: 'showEntityDetails',
      },
      {
        label: 'Addresses',
        value: state.showAddresses,
        property: 'showAddresses',
      },
      {
        label: 'LEI Registration details',
        value: state.showRegistrationDetails,
        property: 'showRegistrationDetails',
      },
      {
        label: 'LOU details',
        value: state.showLOUDetails,
        property: 'showLOUDetails',
      },
      {
        label: 'Fund relationships',
        value: state.showLevelTwoFunds,
        property: 'showLevelTwoFunds',
      },
      {
        label: 'Branch relationships',
        value: state.showLevelTwoBranches,
        property: 'showLevelTwoBranches',
      },
      {
        label: 'Parents',
        value: state.showLevelTwoParents,
        property: 'showLevelTwoParents',
      },
      {
        label: 'Children',
        value: state.showLevelTwoChildren,
        property: 'showLevelTwoChildren',
      },
      // {label: 'Meta data', value: state.showMetaData, property: 'showMetaData'},
      // {label: 'Historical data', value: state.showHistoricalData, property: 'showHistoricalData'}
    ]
  },
  sectionModificationDisplay (state) {
    const sections = [
      {
        label: 'LEI Record changes',
        value: state.showChangeLog,
        property: 'showChangeLog',
        type: 'lei',
      },
      {
        label: 'Relationship Record changes',
        value: state.showRRChanges,
        property: 'showRRChanges',
        type: 'rr',
      },
      {
        label: 'Reporting Exceptions changes',
        value: state.showREPEXChanges,
        property: 'showREPEXChanges',
        type: 'repex',
      },
    ].filter((item) => {
      return state.recordModifications[item.type].data && !isEmpty(state.recordModifications[item.type].data)
    })

    return [
      ...sections,
      {
        label: 'Legal Entity events',
        value: state.showEventsChangeLog,
        property: 'showEventsChangeLog',
        type: 'event',
      },
    ]
  },
  associatedEntity ({ record, handledAssociatedEntity, }) {
    if (record) return parseEntity(handledAssociatedEntity, record.entity.associatedEntity)
  },
  successorEntity ({ record, handledSuccessorEntity, }) {
    if (record) return parseEntity(handledSuccessorEntity, record.entity.successorEntity)
  },
  successorEntities ({ record, rawSuccessorEntities, loading, }) {
    if (record && !loading) {
      return parseSuccessorEntities(rawSuccessorEntities, record.entity.successorEntities)
    }
    return []
  },
  leiIssuerDataOfCurrentEntity ({ record, }, { leiIssuerData, }) {
    return leiIssuerData(record.registration.managingLou)
  },
  leiIssuerData: ({ leiIssuers, }) => (leiIssuerId) => {
    if (!leiIssuers[leiIssuerId]) return
    const { lei, name, marketingName, website, } = leiIssuers[leiIssuerId]
    return [
      {
        label: 'LEI issuer',
        value: `${name} (${marketingName})`,
      },
      {
        label: 'LEI',
        value: lei,
      },
      {
        label: 'Website',
        value: website,
        link: website,
      },
      // {label: 'Organization Type', value: organizationType},
      // {label: 'Description', value: shortDescription}
    ]
  },
  recordRegistrationStatus ({ record, }) {
    return !record ? '' : record.registration.status
  },
  entitySectionIsBlocked (state, { recordRegistrationStatus, }) {
    return prohibitedRegStatuses.has(recordRegistrationStatus)
  },
  entityDetails (
    { record, ra, elf, isin, umbrellaFund, },
    { associatedEntity, successorEntity, entitySectionIsBlocked, successorEntities, }
  ) {
    if (!record) return []
    const getNames = (names) => Array.isArray(names) ? names.map(({ name, }) => name) : ''
    const getOtherNames = (names) => Array.isArray(names) ? names.map(({ name, type, }) => ({
      name: name,
      value: type,
    })) : ''
    const getSuccessorEntities = (entities) => {
      return entities.map((item, index) => {
        if (item.lei && item.name) {
          return {
            value: item,
            jsonPath: `$.entity.successorEntities[${index}].lei`,
          }
        }
        return item.name ? {
          value: item.name,
          jsonPath: `$.entity.successorEntities[${index}].name`,
        } : {
          value: item.lei,
          jsonPath: `$.entity.successorEntities[${index}].name`,
        }
      })
    }

    const { bic, entity, spglobal, mic, ocid, qcc } = record
    const {
      category, transliteratedOtherNames, otherNames,
      status, legalName, registeredAs, registeredAt, jurisdiction, expiration, subCategory, creationDate,
    } = entity
    const businessEntityRegistryLinkerMapper = getEntityBusinessRegistryLinkMapper(registeredAt.id)
    const result = [
      {
        value: umbrellaFund ? `${umbrellaFund.entity.legalName.name} - ${legalName.name}` : legalName.name,
        tooltip: !!umbrellaFund,
        jsonPath: '$.entity.legalName.name',
      },
      {
        value: getOtherNames(otherNames),
        jsonPath: '$.entity.otherNames[*].name',
      },
      {
        value: getOtherNames(transliteratedOtherNames),
        jsonPath: '$.entity.transliteratedOtherNames[*].name',
      },
      {
        value: ra,
        jsonPath: '$.entity.registeredAt',
      },
      {
        value: registeredAs,
        tooltip: !! businessEntityRegistryLinkerMapper,
        jsonPath: '$.entity.registeredAs',
        link: businessEntityRegistryLinkerMapper ? businessEntityRegistryLinkerMapper.getUrl(registeredAs) : '',
        tip: businessEntityRegistryLinkerMapper ? businessEntityRegistryLinkerMapper.getTooltipMessage() : '',
      },
      {
        value: jurisdiction,
        jsonPath: '$.entity.jurisdiction',
      },
      {
        value: category,
        jsonPath: '$.entity.category',
      },
      {
        value: subCategory,
        jsonPath: '$.entity.subCategory',
      },
      {
        value: elf,
        jsonPath: '$.entity.legalForm',
      },
      {
        value: associatedEntity,
        jsonPath: '$.entity.associatedEntity', // TODO
      },
      {
        value: status,
        jsonPath: '$.entity.status',
      },
      {
        value: timeFormat(creationDate),
        jsonPath: '$.entity.creationDate',
      },
      {
        value: expiration.date || expiration.reason ? expiration : null,
        jsonPath: '$.entity.expiration',
      },
      {
        value: successorEntities.length !== 0 ? getSuccessorEntities(successorEntities) :
          successorEntity,
        multiple: successorEntities.length !== 0,
        jsonPath: '$.entity.successorEntity',
        disabled: false,
      },
      {
        value: bic,
        tooltip: true,
        jsonPath: '$.bic',
      },
      {
        value: mic,
        tooltip: true,
        jsonPath: '$.mic',
      },
      {
        value: ocid,
        tooltip: true,
        jsonPath: '$.ocid',
        link: `https://opencorporates.com/companies/${ocid}`,
      },
      {
        value: qcc,
        tooltip: true,
        jsonPath: '$.qcc',
        link: `https://www.qcckyc.com/lei?qccCode=${qcc}`,
      },
      {
        value: isin,
        tooltip: true,
        jsonPath: '$.isin',
      },
      {
        value: spglobal,
        tooltip: true,
        jsonPath: '$.spglobal',
      },
    ]
    return result.map((obj) => (
      obj.hasOwnProperty('disabled') ? { ...obj, } : {
        ...obj,
        disabled: entitySectionIsBlocked,
      }))
  },
  addressesSectionIsBlocked (state, { recordRegistrationStatus, }) {
    return prohibitedRegStatuses.has(recordRegistrationStatus)
  },
  addresses ({ record, }) {
    if (record === null) return []
    const { legalAddress, otherAddresses, headquartersAddress, } = record.entity
    const createAddressBlock = (title, dataRaw, rootPath) => {
      const data = addPath(dataRaw, rootPath)
      return {
        title,
        data,
        rootPath,
      }
    }
    return [ createAddressBlock('Legal', legalAddress, 'legalAddress'), createAddressBlock('Headquarters', headquartersAddress, 'headquartersAddress'), ...otherAddresses.map((address, i) => createAddressBlock('Other Addresses', address, `addresses[${i}]`)), ]
  },
  registrationDetails ({ record, leiIssuers, }) {
    if (record) {
      return parseRegistrationDetails({
        registration: record.registration,
        leiIssuer: leiIssuers[record.registration.managingLou],
      })
    } else {
      return []
    }
  },
  relationshipDetails ({ relationship: { info, }, }) {
    if (info) return parseRelationshipDetails(info.relationship, 'relationship')
  },
  relationshipRegistrationDetails ({ relationship: { info, }, leiIssuers, }) {
    if (info) return parseRegistrationDetails({
      registration: info.registration,
      leiIssuer: leiIssuers[info.registration.managingLou],
      prefix: 'relationship',
    })
  },
  relationshipException ({ relationship: { exception, }, }) {
    if (exception) return parseException(exception)
  },
  getParent: (state) => (type) => {
    if (state[type]) return state[type]
  },
  getEntity: (state) => (type) => {
    if (state[type]) return state[type]
  },
  getParentException: (state) => (type) => {
    if (state[type + 'ReportingException']) return state[type + 'ReportingException']
  },
  getParentRelationshipDetails: (state) => (type, parentEntity) => {
    const { relationship, } = state[type + 'Relationship'] || {}
    const exception = state[type + 'ReportingException'] || {}
    return parseRelationshipDetailsForChallenge({
      ...relationship,
      ...exception,
      parentEntity,
    }, type)
  },
  getParentRegistrationDetails: (state, { getCurrentRelationshipTypes, }) => (type) => {
    const { registration = {}, } = state[type + 'Relationship'] || {}
    let result = parseRegistrationDetails({
      registration,
      parentType: type,
      leiIssuer: state.leiIssuers[registration.managingLou],
    })
    if (getCurrentRelationshipTypes[type] === 'exception' || getCurrentRelationshipTypes[type] === 'nonExisting') {
      result = result.map((obj) => ({
        ...obj,
        disabled: true,
      }))
    }
    return result
  },
  getParentRelationshipPeriods: (state, { getCurrentRelationshipTypes, }) => (type) => {
    const { registration = {}, } = state[type + 'Relationship'] || {}
    let result
    // let result = parseRelationshipPeriods({
    //   registration,
    //   parentType: type,
    // })
    if (getCurrentRelationshipTypes[type] === 'exception') {
      result = result.map((obj) => ({
        ...obj,
        disabled: true,
      }))
    }
    return result
  },
  relationshipDates ({ relationship, }) {
    return [
      {
        label: 'Relationship',
        value: null,
      },
      {
        label: 'Accounting',
        value: null,
      },
      {
        label: 'Document Filing',
        value: null,
      },
    ]
  },
  parentsAreExist ({
    directParent, directParentReportingException,
    ultimateParentReportingException, ultimateParent,
  }) {
    return directParent
      || directParentReportingException
      || ultimateParentReportingException
      || ultimateParent
  },
  parents (state) {
    const parents = [
      {
        parent: state.directParent,
        info: state.directParentRelationship,
        exception: state.directParentReportingException,
        subInfo: 'Direct Parent',
        tip: record.l2DirectParent,
      },
      {
        parent: state.ultimateParent,
        info: state.ultimateParentRelationship,
        exception: state.ultimateParentReportingException,
        subInfo: 'Ultimate Parent',
        tip: record.l2UltimateParent,
      },
    ]
    return parents.map((p) => ({
      ...p,
      tooltip: p.exception ? 'Show Reporting Exception' : 'Show Relationship Data',
      name: getTitleForParentOrExc(p.exception, p.parent),
      registrationStatus: getRegistrationStatusForParentOrExc(p.exception, p.parent),
      subInfo: p.subInfo + (p.exception ? ' Exception reported' : ''),
      available: isAvailableExc(p.exception) || !!p.parent,
    }))
  },

  getInitialRelationsType ({ directParent, ultimateParent, fundManager, umbrellaFund, masterFund, headOffice, }) {
    let result = {}
    result.directParent = directParent ? 'parent' : 'exception'
    result.ultimateParent = ultimateParent ? 'parent' : 'exception'
    result.fundManager = fundManager ? 'parent' : 'nonExisting'
    result.umbrellaFund = umbrellaFund ? 'parent' : 'nonExisting'
    result.masterFund = masterFund ? 'parent' : 'nonExisting'
    result.headOffice = headOffice ? 'parent' : 'nonExisting'
    return result
  },
  getCurrentRelationshipTypes: (state, { getInitialRelationsType, }) => {
    let result = {}
    let { currentRelationshipTypes, } = state.challenge
    result.directParent = currentRelationshipTypes.directParent || getInitialRelationsType.directParent
    result.ultimateParent = currentRelationshipTypes.ultimateParent || getInitialRelationsType.ultimateParent
    result.fundManager = currentRelationshipTypes.fundManager || getInitialRelationsType.fundManager
    result.umbrellaFund = currentRelationshipTypes.umbrellaFund || getInitialRelationsType.umbrellaFund
    result.masterFund = currentRelationshipTypes.masterFund || getInitialRelationsType.masterFund
    result.headOffice = currentRelationshipTypes.headOffice || getInitialRelationsType.headOffice
    return result
  },
}



// WEBPACK FOOTER //
// ./src/store/modules/record/getters.js